import {
  fetchRightsHolders,
  createRightsHolder,
  updateRightsHolder,
} from '@disco/api/rights-data'
import type {
  RightsHolderEntity,
  CreateRightsHolderRequest,
  UpdateRightsHolderRequest,
} from 'types/rights-data'

import { api } from './index'

export const rightsHoldersApi = api
  .enhanceEndpoints({ addTagTypes: ['RightsHolders'] })
  .injectEndpoints({
    endpoints: build => ({
      getAllRightsHolders: build.query<RightsHolderEntity[], void>({
        queryFn: async () => {
          try {
            const response = await fetchRightsHolders({})
            const results = Array.isArray(response)
              ? response
              : (response as unknown as { results: RightsHolderEntity[] })
                  .results ?? []
            return { data: results }
          } catch (error) {
            return { error }
          }
        },
        providesTags: [{ type: 'RightsHolders', id: 'LIST' }],
      }),

      /**
       * Get a single rights holder by ID.
       * First checks if getAllRightsHolders has cached data and looks up from there.
       * Falls back to API request only if cache miss.
       */
      getRightsHolderById: build.query<RightsHolderEntity | null, number>({
        queryFn: async (id, queryApi) => {
          try {
            const selectAllRightsHolders =
              rightsHoldersApi.endpoints.getAllRightsHolders.select()
            const cachedResult = selectAllRightsHolders(queryApi.getState())

            if (cachedResult?.data) {
              const cachedEntity = cachedResult.data.find(
                entity => entity.id === id,
              )
              if (cachedEntity) {
                return { data: cachedEntity }
              }
            }

            // Fall back to API request if not in cache
            const response = await fetchRightsHolders({ ids: String(id) })
            const results = Array.isArray(response)
              ? response
              : (response as unknown as { results: RightsHolderEntity[] })
                  .results ?? []
            return { data: results[0] ?? null }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, id) => [{ type: 'RightsHolders', id }],
      }),
      createRightsHolder: build.mutation<
        RightsHolderEntity,
        CreateRightsHolderRequest
      >({
        queryFn: async data => {
          try {
            const result = await createRightsHolder(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: [{ type: 'RightsHolders', id: 'LIST' }],
      }),
      updateRightsHolder: build.mutation<
        RightsHolderEntity,
        { id: number; data: UpdateRightsHolderRequest }
      >({
        queryFn: async ({ id, data }) => {
          try {
            const result = await updateRightsHolder(id, data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: [{ type: 'RightsHolders', id: 'LIST' }],
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedEntity } = await queryFulfilled
            dispatch(
              rightsHoldersApi.util.upsertQueryData(
                'getRightsHolderById',
                id,
                updatedEntity,
              ),
            )
          } catch {
            // If mutation fails, cache update is skipped
          }
        },
      }),
    }),
  })

export const {
  useGetAllRightsHoldersQuery,
  useGetRightsHolderByIdQuery,
  useCreateRightsHolderMutation,
  useUpdateRightsHolderMutation,
} = rightsHoldersApi
